import { useEffect, useRef, useState } from 'react';
import { ClipboardList, FileText, Link2, Loader2, Mail, Plus, Upload, X } from 'lucide-react';

/**
 * Editor for the intake forms a practitioner asks clients to complete.
 *
 * Each entry is one of three things: a form she built here (Phase 2 — the client fills it in on
 * the site and the answers come back), a named link to someone else's tool, or a file she
 * uploaded. Before any of this it was a bare URL box, so the client received
 * `https://forms.google.com/d/e/1FAIpQLSd…` as the link text and there was no way to hand over a
 * PDF at all (owner, Jun 2026).
 */
const rowStyle = {
  border: '1px solid rgba(0,0,0,0.1)', borderRadius: 8, padding: 10, marginBottom: 8,
  display: 'flex', flexDirection: 'column', gap: 6,
};

export default function IntakeFormsEditor({ forms, onChange, testPrefix, apiFetch }) {
  const fileInput = useRef(null);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [native, setNative] = useState([]);
  const [defaultReturnEmail, setDefaultReturnEmail] = useState('');

  // Her own address, offered as the obvious place for a returned PDF to go.
  useEffect(() => {
    let alive = true;
    apiFetch('/auth/me')
      .then((me) => { if (alive) setDefaultReturnEmail(me?.email || ''); })
      .catch(() => {});
    return () => { alive = false; };
  }, [apiFetch]);

  // The forms she has built here, so she can attach one instead of pasting a link.
  useEffect(() => {
    let alive = true;
    apiFetch('/intake-forms/mine')
      .then((data) => { if (alive) setNative((data.forms || []).filter((f) => f.published)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [apiFetch]);

  // Old profiles stored plain strings; treat them as unnamed links.
  const list = (forms || []).map(f => (typeof f === 'string' ? { name: '', url: f, kind: 'link' } : f));

  const update = (idx, patch) => onChange(list.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const add = () => onChange([...list, { name: '', url: '', kind: 'link', required: false }]);
  const addNative = () => onChange([...list, { name: '', kind: 'native', form_id: '', required: false }]);
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));

  const pickFile = (idx) => {
    setUploadingIdx(idx);
    fileInput.current?.click();
  };

  const upload = async (file) => {
    const idx = uploadingIdx;
    if (!file || idx === null) return;
    const body = new FormData();
    body.append('file', file);
    setUploadError('');
    try {
      const data = await apiFetch('/practitioner/intake-form-file', { method: 'POST', body });
      update(idx, {
        url: data.file_url,
        kind: 'file',
        file_name: data.file_name,
        name: list[idx]?.name || data.file_name.replace(/\.[^.]+$/, ''),
        // Pre-filled, because a document with nowhere to come back to is a dead end.
        return_email: list[idx]?.return_email || defaultReturnEmail || '',
      });
    } catch (err) {
      // Say so on the row. With no catch at all the rejected promise escaped and the dev
      // overlay swallowed the screen instead (owner, Jun 2026).
      setUploadError(err?.message || 'That file could not be uploaded.');
    } finally {
      setUploadingIdx(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <div>
      <input
        ref={fileInput}
        type="file"
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
        hidden
        onChange={e => upload(e.target.files?.[0])}
        data-testid={`${testPrefix}-file-input`}
      />

      {list.map((f, fi) => (
        <div key={fi} style={rowStyle} data-testid={`${testPrefix}-row-${fi}`}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              className="vav-session-input"
              placeholder="Form name — e.g. New Client Health History"
              value={f.name || ''}
              onChange={e => update(fi, { name: e.target.value })}
              data-testid={`${testPrefix}-name-${fi}`}
              style={{ flex: 1 }}
            />
            <button
              className="vav-remove-block"
              onClick={() => remove(fi)}
              data-testid={`${testPrefix}-remove-${fi}`}
            >
              <X size={13} />
            </button>
          </div>

          {f.kind === 'native' ? (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <ClipboardList size={13} style={{ opacity: 0.6, flexShrink: 0 }} />
              <select
                className="vav-session-input"
                value={f.form_id || ''}
                onChange={e => {
                  const chosen = native.find(n => n.id === e.target.value);
                  update(fi, {
                    form_id: e.target.value,
                    timing: chosen?.timing || 'before',
                    name: f.name || chosen?.title || '',
                  });
                }}
                data-testid={`${testPrefix}-native-select-${fi}`}
                style={{ flex: 1 }}
              >
                <option value="">Choose one of your forms…</option>
                {native.map(n => (
                  <option key={n.id} value={n.id}>
                    {n.title} ({n.timing === 'after' ? 'after' : 'before'} the session)
                  </option>
                ))}
              </select>
              <button className="vav-add-block" onClick={() => update(fi, { kind: 'link', form_id: null })}
                      data-testid={`${testPrefix}-switch-link-from-native-${fi}`}>
                <Link2 size={11} /> Use a link instead
              </button>
            </div>
          ) : f.kind === 'file' ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                <FileText size={13} style={{ opacity: 0.6, flexShrink: 0 }} />
                <a href={f.url} target="_blank" rel="noreferrer"
                   style={{ textDecoration: 'underline', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                   data-testid={`${testPrefix}-file-link-${fi}`}>
                  {f.file_name || 'Uploaded file'}
                </a>
                <button className="vav-add-block" onClick={() => update(fi, { kind: 'link', url: '', file_name: null })}
                        data-testid={`${testPrefix}-switch-link-${fi}`}>
                  <Link2 size={11} /> Use a link instead
                </button>
              </div>
              {/* A PDF has no submit button, so the client has to email it back. Without an
                  address they fill it in and then stop — which is exactly what happened to the
                  owner on his own form (Jun 2026). */}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Mail size={13} style={{ opacity: 0.6, flexShrink: 0 }} />
                <input
                  className="vav-session-input"
                  placeholder={defaultReturnEmail
                    ? `Where should they send it back? (${defaultReturnEmail})`
                    : 'Where should they send it back? Your email address'}
                  value={f.return_email || ''}
                  onChange={e => update(fi, { return_email: e.target.value })}
                  data-testid={`${testPrefix}-return-email-${fi}`}
                  style={{ flex: 1 }}
                />
                {defaultReturnEmail && !f.return_email && (
                  <button className="vav-add-block"
                          onClick={() => update(fi, { return_email: defaultReturnEmail })}
                          data-testid={`${testPrefix}-use-my-email-${fi}`}>
                    Use mine
                  </button>
                )}
              </div>
              <p style={{ fontSize: 11, opacity: 0.7, margin: 0 }}
                 data-testid={`${testPrefix}-file-note-${fi}`}>
                {f.return_email
                  ? 'Your client is told to fill this in and email it to that address. File what '
                    + 'comes back under Hub → Client Files so their reminders stop.'
                  : 'An uploaded document is handed over, not filled in here — without an address '
                    + 'your client has nowhere to send it. Attach one of your own forms instead '
                    + 'if you want the answers to come back automatically.'}
              </p>
            </>
          ) : (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                className="vav-session-input"
                placeholder="https://forms.google.com/... or your own form page"
                value={f.url || ''}
                onChange={e => update(fi, { url: e.target.value, kind: 'link' })}
                data-testid={`${testPrefix}-url-${fi}`}
                style={{ flex: 1 }}
              />
              <button className="vav-add-block" onClick={() => pickFile(fi)}
                      disabled={uploadingIdx === fi}
                      data-testid={`${testPrefix}-upload-${fi}`}>
                {uploadingIdx === fi ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />}
                Upload a file
              </button>
            </div>
          )}

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={Boolean(f.required)}
              onChange={e => update(fi, { required: e.target.checked })}
              data-testid={`${testPrefix}-required-${fi}`}
            />
            Required — remind the client the day before if it&apos;s still outstanding
          </label>
        </div>
      ))}

      {uploadError && (
        <p style={{ fontSize: 11, color: '#be123c', marginBottom: 6 }}
           data-testid={`${testPrefix}-upload-error`}>
          {uploadError}
        </p>
      )}

      <button
        className="vav-add-block"
        onClick={add}
        data-testid={`${testPrefix}-add`}
        style={{ alignSelf: 'flex-start' }}
      >
        <Plus size={11} /> Add {list.length > 0 ? 'another ' : ''}form
      </button>
      {native.length > 0 ? (
        <button
          className="vav-add-block"
          onClick={addNative}
          data-testid={`${testPrefix}-add-native`}
          style={{ alignSelf: 'flex-start', marginLeft: 6 }}
        >
          <ClipboardList size={11} /> Attach one of my forms
        </button>
      ) : (
        <span
          data-testid={`${testPrefix}-no-native-hint`}
          style={{ marginLeft: 8, fontSize: 11, opacity: 0.65 }}
        >
          Build a form in Hub → Client Forms and mark it <strong>Live</strong> to attach it here.
        </span>
      )}
    </div>
  );
}
